import { useCallback, useState, useRef } from 'react';
import { Point, Object, TransformState, ObjectTypes } from './useArtHistory';
import { useConfig } from '../../../utils/config';

interface UseDrawToolProps {
  stageRef: React.RefObject<any>;
  panelSize: number;
  selectedTool: string;
  selectedColor: string;
  selectedThickness: number;
  tools: Record<string, string>;
  onPathAdded?: (newObject: Object) => void;
}

interface UseDrawToolReturn {
  isDrawing: boolean;
  currentStroke: Point[];
  handlePointerDown: (e: React.PointerEvent<HTMLDivElement>) => void;
  handlePointerMove: (e: React.PointerEvent<HTMLDivElement>) => void;
  handlePointerUp: (e?: React.PointerEvent<HTMLDivElement>) => void;
}

export const useDrawTool = ({
  stageRef,
  panelSize,
  selectedTool,
  selectedColor,
  selectedThickness,
  tools,
  onPathAdded
}: UseDrawToolProps): UseDrawToolReturn => {
  const config = useConfig();
  const [isDrawing, setIsDrawing] = useState(false);
  const [currentStroke, setCurrentStroke] = useState<Point[]>([]);
  const currentStrokeStartTimeRef = useRef(0);

  const handlePointerDown = useCallback((e: React.PointerEvent<HTMLDivElement>) => {
    if (selectedTool !== tools.draw && selectedTool !== tools.fill) return;
    
    setIsDrawing(true);
    currentStrokeStartTimeRef.current = Date.now();
    
    const stage = stageRef.current?.drawStageRef.current;
    if (!stage) return;
    
    const pos = stage.getPointerPosition();
    if (!pos) return;
    
    // Use the reference size for storing coordinates
    const REFERENCE_SIZE = config.IMAGE_SIZE;
    const scaleFactor = panelSize / REFERENCE_SIZE;
    
    // Adjust position based on stage scale and position
    const adjustedPos = {
      x: pos.x / scaleFactor,
      y: pos.y / scaleFactor,
      pressure: e.pressure || 0.5, // Default pressure if not supported
    };
    
    setCurrentStroke([adjustedPos]);
  }, [selectedTool, panelSize, tools.draw, tools.fill, stageRef, config.IMAGE_SIZE]);

  const handlePointerMove = useCallback((e: React.PointerEvent<HTMLDivElement>) => {
    if (!isDrawing || (selectedTool !== tools.draw && selectedTool !== tools.fill)) return;
    
    const stage = stageRef.current?.drawStageRef.current;
    if (!stage) return;
    
    const pos = stage.getPointerPosition();
    if (!pos) return;
    
    // Use the reference size for storing coordinates
    const REFERENCE_SIZE = config.IMAGE_SIZE;
    const scaleFactor = panelSize / REFERENCE_SIZE;
    
    // Adjust position based on stage scale and position
    const adjustedPos = {
      x: pos.x / scaleFactor,
      y: pos.y / scaleFactor,
      pressure: e.pressure || 0.5, // Default pressure if not supported
    };
    
    setCurrentStroke(prev => [...prev, adjustedPos]);
  }, [isDrawing, selectedTool, panelSize, tools.draw, tools.fill, stageRef, config.IMAGE_SIZE]);

  const handlePointerUp = useCallback(() => {
    if (isDrawing && (selectedTool === tools.draw || selectedTool === tools.fill) && currentStroke.length > 0) {
      // Create a default transform state
      const defaultTransform: TransformState = {
        x: 0,
        y: 0,
        rotation: 0,
        scaleX: 1,
        scaleY: 1
      };
      
      // Create the new object
      const newObject: Object = {
        type: 'stroke' as ObjectTypes,
        data: {
          points: currentStroke,
          thickness: selectedThickness,
          color: selectedColor,
          isFill: selectedTool === tools.fill
        },
        transform: defaultTransform
      };
      
      // Call the callback with the new object
      if (onPathAdded) {
        onPathAdded(newObject);
      }
      
      setCurrentStroke([]);
    }
    
    setIsDrawing(false);
  }, [isDrawing, currentStroke, selectedTool, selectedThickness, selectedColor, tools.draw, tools.fill, onPathAdded]);

  return {
    isDrawing,
    currentStroke,
    handlePointerDown,
    handlePointerMove,
    handlePointerUp
  };
};
